import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AuthState,
  FactorsState,
  MFAActions,
  PatientState,
  getMFASelectionStatus,
  logOut,
  refreshSession,
  selectOrbiAgentAvailable,
  selectPatientState,
} from '@patient-ui/patient-web/store';
import * as authActions from '@patient-ui/patient-web/store';
import * as patientActions from '@patient-ui/patient-web/store';
import { Patient, ReportHeaderSummary } from '@patient-ui/shared/models';
import { ModalComponent } from '@patient-ui/shared-ui/ui';

import { MfaSetupConfirmationComponent } from '../mfa-setup-confirmation/mfa-setup-confirmation.component';

@Component({
  templateUrl: './portal-layout.component.html',
  styleUrls: ['./portal-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortalLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('emailVerifyModalRef') emailVerifyModal!: ModalComponent;
  @HostBinding('class') cssClass = 'd-flex flex-column vh-100';
  @ViewChild('ieNotificationModalRef', { static: true })
  ieNotificationModalRef!: TemplateRef<unknown>;
  mfaStatus$ = this.mfaStore.select(getMFASelectionStatus);
  destroyed = new Subject<void>();
  patient?: Patient;
  authAttemptsExhausted = false;
  isEligibleToViewResults = false;
  serviceError = false;
  reportHeadersExist = false;
  resultSummary!: ReportHeaderSummary;
  primaryAccountHolder?: Patient;
  isProcessing$ = new BehaviorSubject<boolean>(true);
  emailVerifyModalHeaderText = 'Verify Your Email';

  // Orbi AI assistant (PCSA-13157/13158): gate on agent availability — the
  // Split flag AND the patient's identity verification (PCSA-13452), evaluated
  // independently in the store selector. This layout keeps ONLY the flag, for the
  // page-level clearance class below; the entry point and the panel
  // (`patient-ui-orbi-agent-host`) gate themselves on the same selector, and all
  // of the panel's wiring lives in that host so the public layout can mount the
  // agent from the same one element.
  orbiAgentAvailable$ = this.patientStore.select(selectOrbiAgentAvailable);

  constructor(
    private patientStore: Store<PatientState>,
    private modalService: NgbModal,
    private authstore: Store<AuthState>,
    private mfaStore: Store<FactorsState>,
    private activeModal: NgbModal
  ) {}

  ngOnInit(): void {
    this.patientStore
      .select(selectPatientState)
      .pipe(takeUntil(this.destroyed))
      .subscribe((patientState) => {
        this.patient = patientState.primaryAccountHolder;
      });
    this.msIeVersion();
    this.mfaStatus$.pipe(takeUntil(this.destroyed)).subscribe((data) => {
      if (data === null && this.patient?.emailVerified) {
        this.modalService.open(MfaSetupConfirmationComponent, {
          backdrop: 'static',
          keyboard: false,
        });
      }
    });
    this.mfaStore.dispatch(MFAActions.retriveMFAStatus());
  }

  ngAfterViewInit(): void {
    if (!this.patient?.emailVerified) {
      this.emailVerifyModal.open();
    }
  }

  ngOnDestroy(): void {
    // Complete the teardown subject so all `takeUntil(this.destroyed)`
    // subscriptions above (patient state, MFA) unsubscribe —
    // without this they would leak for the lifetime of the app (PR #3136 review).
    this.destroyed.next();
    this.destroyed.complete();
  }

  emailVerifiedHandler(verified: boolean) {
    if (verified) {
      this.patientStore.dispatch(patientActions.setPatientPAHEmailVerified());
      this.activeModal.dismissAll('verified');
    }
  }

  verifyLogOutHandler() {
    this.activeModal.dismissAll('loggingOut');
    this.authstore.dispatch(authActions.logOut({ redirect: '/landing' }));
    sessionStorage.clear();
    window.history.replaceState(null, '', '/');
  }

  modalHeaderChangedHandler(headerText: string) {
    this.emailVerifyModalHeaderText = headerText;
  }

  openNotificationModal() {
    return this.modalService.open(this.ieNotificationModalRef, {
      backdrop: 'static',
      keyboard: false,
    });
  }

  msIeVersion(): boolean {
    const ua = window.navigator.userAgent;
    const msie = ua.indexOf('MSIE ');

    // eslint-disable-next-line no-useless-escape
    if (msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./)) {
      // If Internet Explorer, return version number
      this.openNotificationModal();
      return true;
    } // If another browser, return 0
    else {
      return false;
    }
  }

  handleSession(logoutDecision: boolean) {
    if (logoutDecision) {
      this.authstore.dispatch(logOut({ redirect: '/landing' }));
    } else {
      this.authstore.dispatch(refreshSession());
    }
  }
}
